import Box from '@mui/material/Box';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import FolderIcon from '@mui/icons-material/Folder';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import ShieldIcon from '@mui/icons-material/Shield';
import TuneIcon from '@mui/icons-material/Tune';
import type { FC, ReactNode } from 'react';
import type { WazuhDateRangeOption } from 'src/mindtime/hooks/useWazuhScope';
import type { Column } from 'src/types/table';
import type {
  WazuhActiveResponseActionLabelsQuery,
  WazuhActiveResponseEndpointItem,
  WazuhActiveResponseItem,
  WazuhActiveResponseQuery,
  WazuhActiveResponseSummaryData,
} from 'src/types/mindtime/wazuhActiveResponse';
import type { WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import {
  EDR_COLORS,
  EdrDateTime,
  EdrKpiCard,
  EdrOverflowMenu,
  EdrStatusPill,
  edrMonoStyle,
  edrMutedStyle,
  formatEndpointLabel,
  edrEndpointNameLinkStyle,
  type EdrStatusTone,
} from '../shared/edrPrimitives';
import { buildEdrApiScope } from '../shared/wazuhScopeParams';

const TEAL_ACCENT = '#0D9488';

export type AutomatedActionKey =
  | 'isolate-endpoint'
  | 'kill-process'
  | 'block-source-ip'
  | 'disable-user'
  | 'quarantine-file';

const ACTION_META: Record<
  AutomatedActionKey,
  { label: string; icon: ReactNode; color: string }
> = {
  'isolate-endpoint': {
    label: 'Isolate Endpoint',
    icon: <ShieldIcon style={{ fontSize: 14 }} />,
    color: EDR_COLORS.critical,
  },
  'kill-process': {
    label: 'Kill Process',
    icon: <HighlightOffIcon style={{ fontSize: 14 }} />,
    color: EDR_COLORS.high,
  },
  'block-source-ip': {
    label: 'Block Source IP',
    icon: <BlockIcon style={{ fontSize: 14 }} />,
    color: EDR_COLORS.brand500,
  },
  'disable-user': {
    label: 'Disable User Account',
    icon: <PersonOffIcon style={{ fontSize: 14 }} />,
    color: EDR_COLORS.medium,
  },
  'quarantine-file': {
    label: 'Quarantine File',
    icon: <FolderIcon style={{ fontSize: 14 }} />,
    color: EDR_COLORS.slate500,
  },
};

/** Preview fallback when action-labels API is unavailable (unsubscribed orgs). */
export const ACTIVE_RESPONSE_ACTION_TYPE_FILTER_OPTIONS = Object.entries(ACTION_META).map(
  ([value, meta]) => ({ label: meta.label, value })
);

export const mapDateRangeToRange = (
  dateRange: WazuhDateRangeOption | undefined
): string | undefined => {
  if (dateRange === 'Last 7 days') return '7d';
  if (dateRange === 'Last 30 days') return '30d';
  return undefined;
};

type BuildActiveResponseActionLabelsQueryInput = {
  scope: WazuhScopeParams;
  profileOrgId?: string;
  isReseller: boolean;
  dateRange?: WazuhDateRangeOption;
  pageOrganizationId?: string;
  wazuhAgentId?: string;
  systemWazuhAgentId?: string;
};

export const buildActiveResponseActionLabelsQuery = ({
  scope,
  profileOrgId,
  isReseller,
  dateRange,
  pageOrganizationId,
  wazuhAgentId,
  systemWazuhAgentId,
}: BuildActiveResponseActionLabelsQueryInput): WazuhActiveResponseActionLabelsQuery => {
  const agentId = systemWazuhAgentId?.trim();
  if (agentId) {
    return { systemWazuhAgentId: agentId };
  }

  const range = mapDateRangeToRange(dateRange);
  return {
    ...buildEdrApiScope({
      scope,
      profileOrgId,
      isReseller,
      pageOrganizationId: pageOrganizationId || undefined,
    }),
    ...(wazuhAgentId?.trim() ? { wazuhAgentId: wazuhAgentId.trim() } : {}),
    ...(range ? { range } : {}),
  };
};

export const ACTIVE_RESPONSE_STATUS_FILTER_OPTIONS = [
  { label: 'Success', value: 'success' },
  { label: 'Failed', value: 'failed' },
  { label: 'Pending', value: 'pending' },
];

export const ACTIVE_RESPONSE_SEVERITY_FILTER_OPTIONS = [
  { label: 'Critical', value: 'critical' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

export type ActiveResponseAgentFilters = {
  status?: string;
  actionLabel?: string;
  severityBand?: string;
};

export type ActiveResponseListColumnScope = 'global' | 'endpoint';

export const buildActiveResponseAgentQuery = (
  systemWazuhAgentId: string,
  page: number,
  pageSize: number,
  search: string,
  filters: ActiveResponseAgentFilters = {}
): WazuhActiveResponseQuery => {
  const { status, actionLabel, severityBand } = filters;

  return {
    systemWazuhAgentId,
    page,
    pageSize,
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(status ? { status } : {}),
    ...(actionLabel ? { actionLabel } : {}),
    ...(severityBand ? { severityBand } : {}),
  };
};

const normalizeKey = (value: string | undefined | null): string =>
  (value ?? '').toString().trim().toLowerCase();

export const getActiveResponseId = (
  item: Pick<WazuhActiveResponseItem, 'id' | '_id'> | null | undefined
): string => String(item?.id ?? item?._id ?? '').trim();

export const getActiveResponseTrigger = (item: WazuhActiveResponseItem): string =>
  item.trigger?.trim() || item.triggerEvent?.trim() || '—';

export const getActiveResponseDurationSec = (item: WazuhActiveResponseItem): number | null => {
  if (typeof item.durationSec === 'number' && !Number.isNaN(item.durationSec)) {
    return item.durationSec;
  }
  if (typeof item.duration === 'number' && !Number.isNaN(item.duration)) {
    return item.duration;
  }
  return null;
};

export const normalizeActiveResponseStatus = (
  status: string | undefined | null
): 'success' | 'failed' | 'pending' | 'unknown' => {
  const key = normalizeKey(status);
  if (key === 'success' || key === 'successful') return 'success';
  if (key === 'failed' || key === 'failure') return 'failed';
  if (key === 'pending') return 'pending';
  return 'unknown';
};

export const formatActiveResponseStatusLabel = (status: string | undefined | null): string => {
  const normalized = normalizeActiveResponseStatus(status);
  if (normalized === 'success') return 'Success';
  if (normalized === 'failed') return 'Failed';
  if (normalized === 'pending') return 'Pending';
  return status?.trim() || '—';
};

export const activeResponseStatusToTone = (status: string | undefined | null): EdrStatusTone => {
  const normalized = normalizeActiveResponseStatus(status);
  if (normalized === 'success') return 'success';
  if (normalized === 'failed') return 'critical';
  if (normalized === 'pending') return 'medium';
  return 'neutral';
};

export const mapActionTypeToMeta = (
  actionType: string | undefined | null
): { label: string; icon: ReactNode; color: string } => {
  const key = normalizeKey(actionType).replace(/_/g, '-');
  const known = ACTION_META[key as AutomatedActionKey];
  if (known) return known;

  const label = actionType?.trim()
    ? actionType
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : 'Unknown Action';

  return {
    label,
    icon: <FlashOnIcon style={{ fontSize: 14 }} />,
    color: EDR_COLORS.slate500,
  };
};

const ActionTag: FC<{ actionType?: string | null }> = ({ actionType }) => {
  const meta = mapActionTypeToMeta(actionType);

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: EDR_COLORS.textBody,
      }}
    >
      <span style={{ color: meta.color, display: 'inline-flex' }}>{meta.icon}</span>
      {meta.label}
    </span>
  );
};

export const buildActiveResponseSummaryFromItems = (
  items: WazuhActiveResponseItem[],
  activeRulesCount = 28
): WazuhActiveResponseSummaryData => {
  let successful = 0;
  let failed = 0;
  let pending = 0;

  items.forEach((item) => {
    const status = normalizeActiveResponseStatus(item.status);
    if (status === 'success') successful += 1;
    else if (status === 'failed') failed += 1;
    else if (status === 'pending') pending += 1;
  });

  const total = items.length;

  return {
    total,
    successful,
    failed,
    pending,
    successRate: total ? Math.round((successful / total) * 100) : 0,
    activeRulesCount,
  };
};

const formatKpiNumber = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

export const getActiveResponseSuccessRatePillLabel = (
  summary: WazuhActiveResponseSummaryData | null | undefined
): string => {
  const successRateLabel = summary?.successRateLabel?.trim();
  if (successRateLabel) {
    return successRateLabel.toUpperCase() === 'N/A'
      ? 'Success rate unavailable'
      : `${successRateLabel} success rate`;
  }

  if (typeof summary?.successRate === 'number') {
    return `${Math.round(summary.successRate)}% success rate`;
  }

  if (summary?.total && summary?.successful) {
    return `${Math.round((summary.successful / summary.total) * 100)}% success rate`;
  }

  return 'Success rate unavailable';
};

export const ActiveResponseSummaryKpiGrid: FC<{
  summary: WazuhActiveResponseSummaryData | null;
}> = ({ summary }) => {
  const pendingCount = summary?.pending ?? 0;
  const successRatePillLabel = getActiveResponseSuccessRatePillLabel(summary);

  return (
    <Box display="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
      <EdrKpiCard
        label="Total Responses"
        value={formatKpiNumber(summary?.total)}
        icon={<FlashOnIcon style={{ fontSize: 18 }} />}
        iconColor={EDR_COLORS.brand500}
        accent={EDR_COLORS.brand500}
      >
        <EdrStatusPill tone="brand" label="In selected date range" dot={false} />
      </EdrKpiCard>
      <EdrKpiCard
        label="Successful Responses"
        value={formatKpiNumber(summary?.successful)}
        icon={<CheckCircleOutlineIcon style={{ fontSize: 18 }} />}
        iconColor={EDR_COLORS.success}
        accent={EDR_COLORS.success}
      >
        <EdrStatusPill
          tone="success"
          label={successRatePillLabel}
          dot={false}
        />
      </EdrKpiCard>
      <EdrKpiCard
        label="Failed Responses"
        value={formatKpiNumber(summary?.failed)}
        icon={<HighlightOffIcon style={{ fontSize: 18 }} />}
        iconColor={EDR_COLORS.critical}
        accent={EDR_COLORS.critical}
      >
        <EdrStatusPill
          tone={pendingCount > 0 ? 'medium' : 'critical'}
          label={
            pendingCount > 0
              ? `${pendingCount} pending · require follow-up`
              : 'Require manual follow-up'
          }
          dot={false}
        />
      </EdrKpiCard>
      <EdrKpiCard
        label="Active Response Rules"
        value={formatKpiNumber(summary?.activeRulesCount)}
        icon={<TuneIcon style={{ fontSize: 18 }} />}
        iconColor={TEAL_ACCENT}
        accent={TEAL_ACCENT}
      >
        <EdrStatusPill tone="brand" label="Currently enabled" dot={false} />
      </EdrKpiCard>
    </Box>
  );
};

export const getActiveResponseListColumns = (
  scope: ActiveResponseListColumnScope = 'global',
  onView?: (item: WazuhActiveResponseItem) => void
): Column[] => {
  const isEndpoint = scope === 'endpoint';

  return [
    {
      id: 'trigger',
      label: 'Trigger Event',
      render: (row) => (
        <span style={{ fontWeight: 600, color: EDR_COLORS.textPrimary }}>
          {getActiveResponseTrigger(row as unknown as WazuhActiveResponseItem)}
        </span>
      ),
    },
    {
      id: 'actionType',
      label: 'Automated Action',
      render: (row) => (
        <ActionTag actionType={(row as unknown as WazuhActiveResponseItem).actionType} />
      ),
    },
    ...(isEndpoint
      ? []
      : [
          {
            id: 'endpoint',
            label: 'Endpoint',
            render: (row) => (
              <span style={edrMonoStyle}>
                {(row as unknown as WazuhActiveResponseItem).endpoint?.trim() || '—'}
              </span>
            ),
          },
          {
            id: 'hostName',
            label: 'Endpoint Label',
            render: (row) => (
              <span style={edrMutedStyle}>
                {formatEndpointLabel((row as unknown as WazuhActiveResponseItem).hostName)}
              </span>
            ),
          },
          {
            id: 'organization',
            label: 'Organization',
            render: (row) => {
              const item = row as unknown as WazuhActiveResponseItem;
              return (
                <span style={edrMutedStyle}>
                  {item.organizationName?.trim() || item.organization?.trim() || '—'}
                </span>
              );
            },
          },
        ]),
    {
      id: 'status',
      label: 'Status',
      render: (row) => {
        const item = row as unknown as WazuhActiveResponseItem;
        return (
          <EdrStatusPill
            tone={activeResponseStatusToTone(item.status)}
            label={formatActiveResponseStatusLabel(item.status)}
          />
        );
      },
    },
    {
      id: 'duration',
      label: 'Duration',
      render: (row) => {
        const duration = getActiveResponseDurationSec(row as unknown as WazuhActiveResponseItem);
        return (
          <span style={{ color: EDR_COLORS.textSecondary }}>
            {duration != null ? `${duration} sec` : '—'}
          </span>
        );
      },
    },
    {
      id: 'timestamp',
      label: 'Timestamp',
      render: (row) => (
        <EdrDateTime value={(row as unknown as WazuhActiveResponseItem).timestamp} />
      ),
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      render: (row) => (
        <EdrOverflowMenu
          items={[
            { label: 'View Details' },
            { label: 'View Endpoint' },
            { label: 'Rerun Action' },
            { label: 'Disable Rule', tone: 'danger' },
          ]}
          onView={() => onView?.(row as unknown as WazuhActiveResponseItem)}
        />
      ),
    },
  ];
};

const formatEndpointCount = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

const readEndpointSuccessRate = (item: WazuhActiveResponseEndpointItem): number | null => {
  if (typeof item.successRate === 'number' && !Number.isNaN(item.successRate)) {
    return item.successRate;
  }
  if (typeof item.successRatePercent === 'number' && !Number.isNaN(item.successRatePercent)) {
    return item.successRatePercent;
  }
  if (typeof item.successRatePercent === 'string' && item.successRatePercent.trim()) {
    const numeric = Number(item.successRatePercent.replace(/%/g, ''));
    return Number.isNaN(numeric) ? null : numeric;
  }
  return null;
};

const getSuccessRateColor = (rate: number) => {
  if (rate >= 90) return EDR_COLORS.success;
  if (rate >= 50) return EDR_COLORS.medium;
  return EDR_COLORS.critical;
};

const SuccessRateCell: FC<{ rate: number | null }> = ({ rate }) => {
  if (rate == null) return <span style={edrMutedStyle}>—</span>;
  const color = getSuccessRateColor(rate);
  const pct = Math.min(100, Math.max(0, rate));
  return (
    <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 96 }}>
      <span style={{ ...edrMonoStyle, fontWeight: 700, color, minWidth: 36 }}>
        {`${Math.round(rate)}%`}
      </span>
      <Box
        style={{
          flex: 1,
          height: 4,
          borderRadius: 2,
          background: EDR_COLORS.slate200,
          minWidth: 48,
          maxWidth: 72,
        }}
      >
        <Box style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: color }} />
      </Box>
    </Box>
  );
};

export const getActiveResponseAffectedEndpointListColumns = (): Column[] => [
  {
    id: 'endpoint',
    label: 'Endpoint',
    render: (row) => {
      const item = row as unknown as WazuhActiveResponseEndpointItem;
      const canOpen = Boolean(item.systemWazuhAgentId?.trim());
      return (
        <span style={edrEndpointNameLinkStyle(canOpen)}>
          {item.endpoint ?? item.name ?? '—'}
        </span>
      );
    },
  },
  {
    id: 'hostName',
    label: 'Endpoint Label',
    render: (row) => (
      <span style={edrMutedStyle}>
        {formatEndpointLabel((row as unknown as WazuhActiveResponseEndpointItem).hostName)}
      </span>
    ),
  },
  {
    id: 'organization',
    label: 'Organization',
    render: (row) => {
      const item = row as unknown as WazuhActiveResponseEndpointItem;
      return (
        <span style={edrMutedStyle}>
          {item.organization ?? item.organizationName ?? '—'}
        </span>
      );
    },
  },
  {
    id: 'responses',
    label: 'Responses',
    align: 'center',
    render: (row) => {
      const item = row as unknown as WazuhActiveResponseEndpointItem;
      return (
        <span style={{ fontWeight: 700, color: EDR_COLORS.textPrimary }}>
          {formatEndpointCount(item.responses ?? item.totalResponses)}
        </span>
      );
    },
  },
  {
    id: 'successful',
    label: 'Successful',
    align: 'center',
    render: (row) => {
      const item = row as unknown as WazuhActiveResponseEndpointItem;
      return (
        <span style={{ fontWeight: 600, color: EDR_COLORS.success }}>
          {formatEndpointCount(item.successful ?? item.successfulResponses)}
        </span>
      );
    },
  },
  {
    id: 'failed',
    label: 'Failed',
    align: 'center',
    render: (row) => {
      const item = row as unknown as WazuhActiveResponseEndpointItem;
      return (
        <span style={{ fontWeight: 600, color: EDR_COLORS.critical }}>
          {formatEndpointCount(item.failed ?? item.failedResponses)}
        </span>
      );
    },
  },
  {
    id: 'successRate',
    label: 'Success Rate',
    render: (row) => (
      <SuccessRateCell
        rate={readEndpointSuccessRate(row as unknown as WazuhActiveResponseEndpointItem)}
      />
    ),
  },
];
